import { useMemo, type CSSProperties, type ReactElement } from 'react';
import { RuleOriginTag } from './RuleOriginTag';
import type { SubmissionValidationResultsResponse } from '@/api/v2Client';
import { MovableDialog } from '@/components/shared/MovableDialog';
import { formatDateTime } from '@/lib/format';
import { summarizeValidationReport } from '@/lib/validationSummary';

/**
 * Phase UI-13 — Validation report modal.
 *
 * Opened from the "View Report" button on the ValidationStatusBanner.
 * Lists every validation result in a table sorted errors-first, with a
 * per-warning "Suppress" action that bubbles up to the caller for
 * actual persistence.
 */

export interface ValidationReportModalProps {
  readonly isOpen: boolean;
  readonly data: SubmissionValidationResultsResponse | null;
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly onRetry?: () => void;
  readonly onClose: () => void;
  readonly onSuppress?: (resultId: string) => void;
}

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
};

const thStyle: CSSProperties = {
  textAlign: 'left',
  borderBottom: '1px solid var(--border)',
  padding: '6px 8px',
  fontWeight: 600,
  background: 'var(--surface2)',
  color: 'var(--text)',
};

const tdStyle: CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'top',
};

const severityBadge = (sev: string): CSSProperties => {
  const base: CSSProperties = {
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
  };
  if (sev === 'error') {
    return { ...base, background: 'rgba(239,68,68,0.18)', color: '#b91c1c' };
  }
  if (sev === 'warning') {
    return { ...base, background: 'rgba(245,158,11,0.18)', color: '#92400e' };
  }
  return { ...base, background: 'var(--surface2)', color: 'var(--text-muted)' };
};

const outcomeLabels = { pass: 'Passed', fail: 'Failed', skip: 'Skipped' } as const;
const outcomeColors = { pass: '#15803d', fail: '#b91c1c', skip: 'var(--text-muted)' };
const currencyLabels = {
  never: 'Validation has not been run yet. Run validation to create a report.',
  'documents-only': 'Only document checks have run. Run full validation to check the submission.',
  stale: 'These results are out of date. The submission changed after validation; run validation again.',
  'cannot-tell': 'Could not determine whether these results cover the current submission.',
  current: 'These results cover the current submission.',
} as const;

export function ValidationReportModal({
  isOpen,
  data,
  loading = false,
  error = null,
  onRetry,
  onClose,
  onSuppress,
}: ValidationReportModalProps): ReactElement | null {
  const { counts, ordered } = useMemo(() => summarizeValidationReport(data?.results ?? []), [data]);

  if (!isOpen) return null;

  // phase-movable-dialogs — the ARTIFACT report in the shared movable shell,
  // resizable (a large table benefits from a bigger window).
  return (
    <MovableDialog
      persistKey="compiler.validationReport"
      title="Validation report"
      testId="validation-report-modal"
      onClose={onClose}
      resizable
      minSize={{ width: 640, height: 420 }}
      width="min(1200px, 92vw)"
      maxHeight="85vh"
      zIndex={1000}
      headerRight={
        <button
          type="button"
          data-testid="validation-report-close"
          onClick={onClose}
          style={{
            background: 'none',
            border: '1px solid var(--border)',
            borderRadius: 4,
            padding: '4px 12px',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          Close
        </button>
      }
    >
        {loading ? (
          <p role="status">Loading validation results…</p>
        ) : error ? (
          <div role="alert">
            <p>Could not load validation results: {error}</p>
            {onRetry && <button type="button" onClick={onRetry}>Retry</button>}
          </div>
        ) : !data ? (
          <p data-testid="validation-report-unavailable">Validation results are unavailable.</p>
        ) : <>
          {data.currency && (
            <p data-testid="validation-report-currency" style={{ margin: '0 0 12px' }}>
              {data.currency.detail || currencyLabels[data.currency.kind]}
              {data.currency.lastFullRunAt && <><br />Last full validation: {formatDateTime(data.currency.lastFullRunAt)}</>}
            </p>
          )}
          {ordered.length === 0 ? (
          <p data-testid="validation-report-empty" style={{ margin: 0 }}>
            {data.currency?.kind === 'never'
              ? 'No checks have run yet.'
              : 'No recorded validation results are available. Run validation to create a report.'}
          </p>
        ) : (
          <>
          <p data-testid="validation-report-summary" style={{ margin: '0 0 8px', fontWeight: 600 }}>
            {counts.fail} failed · {counts.pass} passed · {counts.skip} skipped · {ordered.length} total checks
          </p>
          <p style={{ margin: '0 0 12px', color: 'var(--text-muted)', fontSize: 12 }}>
            Latest recorded result for each check and target. Skipped checks were not evaluated; see the message for the reason.
            {' '}Severity describes the rule if it fails, not the outcome.
          </p>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Result</th>
                <th style={thStyle}>Severity if failed</th>
                <th style={thStyle}>Validator</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Target</th>
                <th style={thStyle}>Message</th>
                <th style={thStyle}>Checked</th>
                {onSuppress && <th style={thStyle}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {ordered.map((r) => (
                <tr key={r.resultId} data-testid={`validation-report-row-${r.resultId}`}>
                  <td style={{ ...tdStyle, color: outcomeColors[r.status], fontWeight: 600 }}>
                    {outcomeLabels[r.status]}
                  </td>
                  <td style={tdStyle}>
                    <span style={r.status === 'fail' ? severityBadge(r.severity) : { color: 'var(--text-muted)' }}>{r.severity}</span>
                  </td>
                  <td style={tdStyle}>
                    <code style={{ fontSize: 11 }}>{r.ruleName}</code>
                    <RuleOriginTag origin={r.origin} />
                  </td>
                  <td style={tdStyle}>{r.description}</td>
                  <td style={{ ...tdStyle, minWidth: 140 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.targetType}</div>
                    <code style={{ fontSize: 11, overflowWrap: 'anywhere' }}>{r.targetId}</code>
                  </td>
                  <td style={tdStyle}>{r.message ?? '—'}</td>
                  <td style={tdStyle}>{r.validatedAt ? formatDateTime(r.validatedAt) : '—'}</td>
                  {onSuppress && <td style={tdStyle}>
                    {r.status === 'fail' && r.severity === 'warning' ? (
                      <button
                        type="button"
                        data-testid={`suppress-warning-${r.resultId}`}
                        onClick={() => onSuppress(r.resultId)}
                        style={{
                          padding: '2px 8px',
                          fontSize: 11,
                          border: '1px solid var(--border)',
                          borderRadius: 3,
                          background: 'var(--surface)',
                          cursor: 'pointer',
                        }}
                      >
                        Suppress
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
                    )}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
        </>}
    </MovableDialog>
  );
}
